<template>
  <div>
    <app-cells>
      <h1 class="title">Staff Loans</h1>
      <app-button @click.native="onShowModal">Create Loan</app-button>
    </app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Active Loans:</span> <span>{{ loans_summary.active_loans_count }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Pending Requests:</span> <span>{{ loans_summary.pending_requests }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Fully Paid:</span> <span>{{ loans_summary.fully_paid_count }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Archived:</span> <span>{{ loans_summary.archived_count }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Abandoned:</span> <span>{{ loans_summary.abandoned_count }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Requested Amount:</span> <span>{{ loans_summary.requested_amount.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Total Disbursed:</span> <span>{{ loans_summary.total_disbursed_lifetime.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Total Outstanding:</span> <span>{{ loans_summary.total_outstanding.toLocaleString() }} TSh</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Average Loan Size:</span> <span>{{ loans_summary.average_loan_size.toLocaleString() }} TSh</span>
            </div>
          </div>
        </div>
      </div>
      <app-tabs :tabs="tabs"/>
      <transition mode="out-in" name="fade">
        <router-view
          ref="activeLoans"
          @confirm-action="onShowActionConfirmation"
          @edit-loan="onEditLoan"
          @refresh-summary="fetchSummary"
        />
      </transition>
    </template>
    <modal
      name="create_loans"
      :width="960"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      @closed="resetEligibilityPreview"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="closeLoanModal" class="popup__close" type="button"></button>
        <div class="popup__title">{{ editingLoanId ? 'Edit Staff Loan' : 'Create Staff Loan' }}</div>
        <div class="popup__body staff-loans__modal-body">
          <form @submit.prevent="onCheckForm">
            <app-form-group v-if="!editingLoanId" label="Employee" required>
              <app-select
                @search="onEmployeeSearch"
                v-model="form.employee"
                :options="employee_options"
                :clearable="false"
                label="full_name"
                placeholder="Employee"
                :error="$v.form.employee.$error"
              />
              <template #error>
                <div v-if="$v.form.employee.$dirty && !$v.form.employee.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Amount" label-for="amount" required>
              <app-fin-number
                v-model="form.amount"
                :error="$v.form.amount.$error"
                id="amount"
                name="amount"
                placeholder="0"
              />
              <template #error>
                <div v-if="$v.form.amount.$dirty && !$v.form.amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Installments" label-for="installments" required>
              <div class="staff-loans__installments">
                <app-radio
                  v-for="option in installment_options"
                  :key="option"
                  v-model.number="form.installments"
                  :checked="form.installments === option"
                  :error="$v.form.installments.$error"
                  :value="option"
                  class="staff-loans__installment-option"
                  name="installments"
                >
                  <template #label>{{ option }}</template>
                </app-radio>
              </div>
              <template #error>
                <div v-if="$v.form.installments.$dirty && !$v.form.installments.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Employee notes" label-for="employee_notes">
              <app-textarea
                v-model="form.employee_notes"
                id="employee_notes"
                name="employee_notes"
                placeholder="Employee notes"
              />
            </app-form-group>
            <app-form-group label="Employer notes" label-for="employer_notes">
              <app-textarea
                v-model="form.employer_notes"
                id="employer_notes"
                name="employer_notes"
                placeholder="Employer notes"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button v-if="canSubmitLoan" ref="loan_create" type="submit">
                {{ editingLoanId ? 'Save' : 'Create' }}
              </app-button>
            </app-cells>
          </form>
          <div class="staff-loans__eligibility">
            <div v-if="eligibility_preview_loading && eligibility_preview" class="staff-loans__eligibility-loader">
              <img alt="" src="@/assets/img/preloader.svg">
            </div>
            <template v-if="eligibility_preview">
              <div class="staff-loans__eligibility-head">
                <div class="staff-loans__eligibility-title">Eligibility status</div>
                <span
                  :class="{ 'staff-loans__eligibility-status--error': !eligibility_preview.eligible }"
                  class="staff-loans__eligibility-status"
                >
                  {{ eligibility_preview.eligible ? 'Eligible' : 'Not eligible' }}
                </span>
              </div>
              <div class="staff-loans__eligibility-main">
                <div class="staff-loans__eligibility-label">Salary after request</div>
                <div class="staff-loans__eligibility-value staff-loans__eligibility-value--large">
                  {{ formatAmount(eligibility_preview.accrued_salary_after) }}
                </div>
                <div class="staff-loans__eligibility-hint">
                  {{ eligibility_preview.accrued_salary_percent_after }}% after request
                </div>
              </div>
              <div class="staff-loans__eligibility-grid">
                <div class="staff-loans__eligibility-card">
                  <div class="staff-loans__eligibility-label">Principal</div>
                  <div class="staff-loans__eligibility-value">{{ formatAmount(form.amount) }}</div>
                </div>
                <div class="staff-loans__eligibility-card">
                  <div class="staff-loans__eligibility-label">Interest ({{ eligibility_preview.interest_percent }}% p.a.)</div>
                  <div class="staff-loans__eligibility-value">{{ formatAmount(eligibility_preview.interest_amount) }}</div>
                </div>
                <div class="staff-loans__eligibility-card">
                  <div class="staff-loans__eligibility-label">Total repayment</div>
                  <div class="staff-loans__eligibility-value">{{ formatAmount(eligibility_preview.total_repayment_amount) }}</div>
                </div>
                <div class="staff-loans__eligibility-card">
                  <div class="staff-loans__eligibility-label">Gross salary</div>
                  <div class="staff-loans__eligibility-value">{{ formatAmount(eligibility_preview.gross_salary) }}</div>
                </div>
                <div class="staff-loans__eligibility-card">
                  <div class="staff-loans__eligibility-label">Net salary before loans/advance</div>
                  <div class="staff-loans__eligibility-value">{{ formatAmount(eligibility_preview.net_salary_before_debt) }}</div>
                </div>
              </div>
              <div class="staff-loans__eligibility-request">
                <div class="staff-loans__eligibility-label">Requested monthly deduction</div>
                <div class="staff-loans__eligibility-value staff-loans__eligibility-value--large">
                  {{ formatAmount(eligibility_preview.new_monthly_payment) }} / {{ formatAmount(eligibility_preview.available_monthly_deduction) }}
                </div>
                <div class="staff-loans__eligibility-hint">requested / available</div>
              </div>
              <div
                v-if="eligibility_preview.heslb_amount > 0 || eligibility_preview.existing_staff_loan_amount > 0 || eligibility_preview.existing_advance_amount > 0"
                class="staff-loans__eligibility-deductions"
              >
                <div v-if="eligibility_preview.heslb_amount > 0" class="staff-loans__eligibility-deduction">
                  Existing HESLB deduction: <b>{{ formatAmount(eligibility_preview.heslb_amount) }}</b>
                </div>
                <div v-if="eligibility_preview.existing_staff_loan_amount > 0" class="staff-loans__eligibility-deduction">
                  Existing staff loan deductions: <b>{{ formatAmount(eligibility_preview.existing_staff_loan_amount) }}</b>
                </div>
                <div v-if="eligibility_preview.existing_advance_amount > 0" class="staff-loans__eligibility-deduction">
                  Existing advance deductions: <b>{{ formatAmount(eligibility_preview.existing_advance_amount) }}</b>
                </div>
              </div>
              <div v-if="!eligibility_preview.eligible" class="staff-loans__eligibility-reason">
                <b>Reason:</b> {{ eligibility_preview.reason && eligibility_preview.reason.name }}
              </div>
              <div v-if="eligibility_preview.installment_amounts && eligibility_preview.installment_amounts.length" class="staff-loans__schedule-preview">
                <div class="staff-loans__schedule-title">Preview schedule</div>
                <table class="staff-loans__schedule-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Month</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="installment in eligibility_preview.installment_amounts" :key="installment.sequence">
                      <td>{{ installment.sequence }}</td>
                      <td>{{ formatMonth(installment.related_month) }}</td>
                      <td>{{ formatNumber(installment.principal_amount) }}</td>
                      <td>{{ formatNumber(installment.interest_amount) }}</td>
                      <td>{{ formatNumber(installment.amount) }}</td>
                    </tr>
                    <tr class="staff-loans__schedule-total">
                      <td colspan="2">Total</td>
                      <td>{{ formatNumber(schedulePrincipalTotal) }}</td>
                      <td>{{ formatNumber(scheduleInterestTotal) }}</td>
                      <td>{{ formatNumber(scheduleTotal) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <div v-else-if="eligibility_preview_loading" class="staff-loans__eligibility-placeholder">
              <app-spinner v-if="eligibility_preview_initial_loading"/>
              <img v-else alt="" src="@/assets/img/preloader.svg">
            </div>
            <div v-else-if="eligibility_preview_error" class="staff-loans__eligibility-placeholder">
              Unable to load eligibility preview
            </div>
            <div v-else class="staff-loans__eligibility-placeholder">
              Select employee, amount and installments to see eligibility preview
            </div>
          </div>
        </div>
      </div>
    </modal>
    <modal
      name="staff-loan-action-confirmation"
      :width="400"
      :height="'auto'"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="cancelAction" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex"><img src="@/assets/img/warning-round-icon.svg" alt=""> Confirm action</div>
        <div class="popup__body">
          <div class="popup__subt">
            Are you sure you want to {{ pendingAction ? pendingAction.action.label.toLowerCase() : '' }} this loan?
          </div>
          <app-cells :indent="false" position="center">
            <app-button theme="dark" @click.native="cancelAction">Cancel</app-button>
            <app-button
              ref="action_preload"
              @click.native="confirmAction"
            >
              Confirm
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getEmployeesStaffLoansEligibilityPreview,
  getEmployeesStaffLoansSummary,
  getEmployeesListSearch,
  patchEmployeeStaffLoans,
  postEmployeeStaffLoans,
  postEmployeesStaffLoansAbandon,
  postEmployeesStaffLoansApprove,
  postEmployeesStaffLoansCancel,
  postEmployeesStaffLoansDisburse,
  postEmployeesStaffLoansMarkFullyPaid,
  postEmployeesStaffLoansReject,
} from '@/http'
import AppTabs from '@/components/AppTabs'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'

const actionRequests = {
  approve: postEmployeesStaffLoansApprove,
  reject: postEmployeesStaffLoansReject,
  cancel: postEmployeesStaffLoansCancel,
  disburse: postEmployeesStaffLoansDisburse,
  markFullyPaid: postEmployeesStaffLoansMarkFullyPaid,
  abandon: postEmployeesStaffLoansAbandon,
}

export default {
  name: 'StaffLoansLayout',
  components: {AppTabs},
  data() {
    return {
      tabs: [
        {name: 'employees-staff-loans', label: 'Requests'},
        {name: 'employees-staff-loans-active', label: 'Active'},
        {name: 'employees-staff-loans-archive', label: 'Archive'}
      ],
      loans_summary: {},
      page_loaded: false,
      form: {
        employee: null,
        amount: null,
        installments: null,
        employee_notes: '',
        employer_notes: '',
      },
      employee_options: [],
      installment_options: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      editingLoanId: null,
      eligibility_preview: null,
      eligibility_preview_loading: false,
      eligibility_preview_initial_loading: false,
      eligibility_preview_error: false,
      eligibilityPreviewRequestId: 0,
      eligibilityPreviewTimeout: null,
      actionLoadingId: null,
      pendingAction: null,
    }
  },
  validations: {
    form: {
      employee: { required },
      amount: { required },
      installments: { required },
    }
  },
  created() {
    this.fetchSummary()
  },
  computed: {
    scheduleTotal() {
      return this.eligibility_preview.installment_amounts.reduce((total, installment) => total + Number(installment.amount || 0), 0)
    },
    schedulePrincipalTotal() {
      return this.eligibility_preview.installment_amounts.reduce((total, installment) => total + Number(installment.principal_amount || 0), 0)
    },
    scheduleInterestTotal() {
      return this.eligibility_preview.installment_amounts.reduce((total, installment) => total + Number(installment.interest_amount || 0), 0)
    },
    eligibilityIsIneligible() {
      return this.eligibility_preview && this.eligibility_preview.eligible === false
    },
    canSubmitLoan() {
      return Boolean(this.eligibility_preview?.eligible) && !this.$v.form.$invalid
    },
  },
  watch: {
    'form.employee': 'onEligibilityParamsChange',
    'form.amount': 'onEligibilityParamsChange',
    'form.installments': 'onEligibilityParamsChange',
  },
  methods: {
    fetchSummary() {
      return getEmployeesStaffLoansSummary().then(response => {
        this.loans_summary = response.data
        this.page_loaded = true
      })
    },
    onShowModal() {
      this.editingLoanId = null
      this.form = {}
      this.resetEligibilityPreview()
      this.$v.form.$reset()
      this.$modal.show('create_loans')
    },
    onEditLoan(row) {
      this.editingLoanId = row.id
      this.form = {
        employee: row.employee,
        amount: row.amount,
        installments: Number(row.installments),
        employee_notes: row.employee_notes,
        employer_notes: row.employer_notes,
      }
      this.resetEligibilityPreview()
      this.eligibility_preview_loading = true
      this.eligibility_preview_initial_loading = true
      this.$v.form.$reset()
      this.$modal.show('create_loans')
    },
    onShowActionConfirmation(payload) {
      if (this.actionLoadingId !== null) return

      this.pendingAction = payload
      this.$modal.show('staff-loan-action-confirmation')
    },
    cancelAction() {
      if (this.actionLoadingId !== null) return

      this.pendingAction = null
      this.$modal.hide('staff-loan-action-confirmation')
    },
    confirmAction() {
      if (!this.pendingAction || this.actionLoadingId !== null) return
      this.$refs.action_preload.preload = true

      const { row, action } = this.pendingAction
      this.actionLoadingId = row.id
      actionRequests[action.code](row.id)
        .then(() => {
          this.$modal.hide('staff-loan-action-confirmation')
          this.pendingAction = null
          this.fetchSummary()
          if (this.$refs.activeLoans && this.$refs.activeLoans.refreshAfterAction) {
            return this.$refs.activeLoans.refreshAfterAction()
          }
          if (this.$refs.activeLoans && this.$refs.activeLoans.fetchItems) {
            return this.$refs.activeLoans.fetchItems()
          }
        })
        .then(() => {
          this.actionLoadingId = null
        })
        .catch(() => {
          this.actionLoadingId = null
          this.$refs.action_preload.preload = false
        })
    },
    onCheckForm() {
      if (this.eligibilityIsIneligible) {
        this.$notify({
          type: 'info',
          title: 'This loan is not eligible',
        })
        return
      }

      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    closeLoanModal() {
      this.$modal.hide('create_loans')
    },
    resetEligibilityPreview() {
      clearTimeout(this.eligibilityPreviewTimeout)
      this.eligibilityPreviewTimeout = null
      this.eligibilityPreviewRequestId += 1
      this.eligibility_preview = null
      this.eligibility_preview_loading = false
      this.eligibility_preview_initial_loading = false
      this.eligibility_preview_error = false
    },
    onEligibilityParamsChange() {
      if (!this.hasEligibilityParams()) {
        this.resetEligibilityPreview()
        return
      }

      this.scheduleEligibilityPreview()
    },
    scheduleEligibilityPreview() {
      clearTimeout(this.eligibilityPreviewTimeout)
      this.eligibilityPreviewTimeout = setTimeout(() => this.fetchEligibilityPreview(), 350)
    },
    fetchEligibilityPreview() {
      if (!this.hasEligibilityParams()) {
        this.resetEligibilityPreview()
        return
      }

      const employeeId = this.form.employee.id
      const amount = Number(this.form.amount)
      const installments = Number(this.form.installments)

      const requestId = this.eligibilityPreviewRequestId + 1
      this.eligibilityPreviewRequestId = requestId
      this.eligibility_preview_error = false
      this.eligibility_preview_loading = true

      getEmployeesStaffLoansEligibilityPreview(employeeId, amount, installments)
        .then(response => {
          if (requestId !== this.eligibilityPreviewRequestId) return
          this.eligibility_preview = response.data
        })
        .catch(() => {
          if (requestId !== this.eligibilityPreviewRequestId) return
          this.eligibility_preview_error = true
        })
        .then(() => {
          if (requestId === this.eligibilityPreviewRequestId) {
            this.eligibility_preview_loading = false
            this.eligibility_preview_initial_loading = false
          }
        })
    },
    hasEligibilityParams() {
      const employeeId = this.form.employee && this.form.employee.id
      const amount = Number(this.form.amount)
      const installments = Number(this.form.installments)

      return Boolean(employeeId) && Number.isFinite(amount) && amount > 0 && Number.isFinite(installments) && installments > 0
    },
    formatAmount(amount) {
      return `${Number(amount || 0).toLocaleString()} TSh`
    },
    formatNumber(amount) {
      return Number(amount || 0).toLocaleString()
    },
    formatMonth(date) {
      if (!date) return '-'
      return format(parseISO(date), 'MMM yyyy')
    },
    sendForm() {
      this.$refs.loan_create.preload = true
      const data = {
        amount: this.form.amount,
        installments: this.form.installments,
        employee_notes: this.form.employee_notes,
        employer_notes: this.form.employer_notes,
      }
      const request = this.editingLoanId ? patchEmployeeStaffLoans(this.editingLoanId, data) : postEmployeeStaffLoans({...data, employee: this.form.employee.id})

      request
        .then(() => {
          if (this.$refs.activeLoans && this.$refs.activeLoans.fetchItems) {
            this.$refs.activeLoans.fetchItems()
          }
          this.fetchSummary()
          this.$notify({
            type: 'success',
            title: this.editingLoanId ? 'Loan updated' : 'Loan created',
          })
          this.$modal.hide('create_loans')
        })
        .catch(() => { this.$refs.loan_create.preload = false })
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.employee_options = []
      } else {
        getEmployeesListSearch({search}).then(response => {
          vm.employee_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>

<style lang="sass">
.staff-loans__installments
  display: flex
  flex-wrap: wrap
  gap: 8px

.staff-loans__installment-option
  width: 32px
  height: 32px
  padding-left: 0

  .radio__text
    z-index: 1
    width: 100%
    justify-content: center
    color: #fff
    pointer-events: none

  .radio__radiomark
    top: 0
    left: 0
    width: 100%
    height: 100%
    border: 0
    border-radius: 4px
    background-color: #92c53d
    transition: background-color 0.2s

  input
    &:checked ~ .radio__radiomark
      border: 0
      background-color: #77b017

  &:hover
    .radio__radiomark
      background-color: #77b017

  &.radio--error
    .radio__radiomark
      border: 1px solid $color-error

.staff-loans__modal-body
  display: grid
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr)
  gap: 24px

.staff-loans__eligibility
  position: relative
  min-height: 240px
  padding: 16px
  border: 1px solid #d9e0e8
  border-radius: 8px
  background: #f8fafc

.staff-loans__eligibility-head
  display: flex
  align-items: center
  justify-content: space-between
  margin-bottom: 14px

.staff-loans__eligibility-title,
.staff-loans__schedule-title
  color: #606770
  font-weight: 700
  text-transform: uppercase

.staff-loans__eligibility-status
  padding: 7px 10px
  border-radius: 5px
  color: #398c62
  background: #dcf3df
  font-weight: 700

.staff-loans__eligibility-status--error
  color: #c84a4a
  background: #fbe1e1

.staff-loans__eligibility-main,
.staff-loans__eligibility-card,
.staff-loans__eligibility-request
  padding: 14px
  border: 1px solid #d9e0e8
  border-radius: 8px
  background: #fff

.staff-loans__eligibility-main
  margin-bottom: 14px

.staff-loans__eligibility-grid
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 12px
  margin-bottom: 14px

.staff-loans__eligibility-request
  border-color: #dce9bd
  background: #fbfff3

.staff-loans__eligibility-label,
.staff-loans__eligibility-hint
  color: #747b84

.staff-loans__eligibility-value
  margin-top: 4px
  font-size: 20px
  font-weight: 700

.staff-loans__eligibility-value--large
  font-size: 25px

.staff-loans__eligibility-deductions,
.staff-loans__eligibility-reason,
.staff-loans__schedule-preview
  margin-top: 16px
  padding-top: 16px
  border-top: 1px solid #d9e0e8

.staff-loans__eligibility-deduction + .staff-loans__eligibility-deduction
  margin-top: 8px

.staff-loans__eligibility-reason
  color: #c84a4a

.staff-loans__schedule-title
  margin-bottom: 8px
  text-transform: none

.staff-loans__schedule-table
  width: 100%
  border-collapse: collapse

  th,
  td
    padding: 8px 4px
    border-bottom: 1px solid #e7ebf0
    text-align: left

  th:nth-child(n + 3),
  td:nth-child(n + 3)
    text-align: right

.staff-loans__schedule-total td
  font-weight: 700

.staff-loans__eligibility-placeholder
  display: flex
  align-items: center
  justify-content: center
  height: 100%
  min-height: 208px
  color: #747b84
  text-align: center

  img
    width: 32px
    height: 32px

.staff-loans__eligibility-loader
  position: absolute
  z-index: 1
  top: 0
  right: 0
  bottom: 0
  left: 0
  display: flex
  align-items: center
  justify-content: center
  border-radius: 8px
  background: rgba(248, 250, 252, 0.7)

  img
    width: 36px
    height: 36px

@media (max-width: 767px)
  .staff-loans__modal-body
    grid-template-columns: 1fr
</style>
